// First-run gate (docs/ONBOARDING-2026-10.md, section 3). This is the only onboarding code in
// the main bundle. checkOnboarding() runs first thing at boot and reads localStorage once;
// for a returning visitor it returns null and nothing else happens (no listeners, no timers,
// no requests). For a first visit, initOnboarding() waits for load + idle + 2.5 s with the
// start screen untouched, then loads the welcome chip (its own JS + CSS, never precached).
import {
	ONBOARDING_KEY, ONBOARDING_LEGACY_KEY, ONBOARDING_VERSION, ONBOARDING_RESHOW_BELOW, ONBOARDING_MAX_OFFERS,
	SETTINGS_KEY, FAVORITES_KEY, HISTORY_KEY, SESSION_KEY,
} from "../shared/settings-schema.js";
import { readJSON, writeJSON } from "../shared/store.js";

const live = (r) => r.status === "pending" || r.status === "offered" || r.status === "snoozed";

// Is the welcome flow still ahead of, or in front of, the visitor on this page load? The slots
// gate (slots-gate.js) waits for it, so a first-time visitor never meets an ad before the app.
// "waiting" = eligible and not shown yet, "shown" = chip or setup dialog on screen.
let welcome = "none";
const settled = [];
function settle() {
	if (welcome === "none") return;
	welcome = "none";
	for (const f of settled.splice(0)) { try { f(); } catch { /* a listener must not block the others */ } }
}
export function whenWelcomeClosed(cb) { if (welcome === "none") cb(); else settled.push(cb); }
// After the lazy bundle starts: settled once the welcome's own top-level elements (the chip is a
// <section>, the setup a <dialog>, both added straight to <body>) have all left the page. Matched
// by tag, not class, so the app bundle carries none of the welcome's markup.
function watchWelcome(before) {
	const mine = new Set([...document.body.children].filter((n) => !before.has(n) && /^(SECTION|DIALOG)$/.test(n.tagName)));
	const mo = new MutationObserver((recs) => {
		for (const r of recs) {
			for (const n of r.addedNodes) if (n.nodeType === 1 && /^(SECTION|DIALOG)$/.test(n.tagName)) mine.add(n);
			for (const n of r.removedNodes) mine.delete(n);
		}
		if (!mine.size) { mo.disconnect(); settle(); }
	});
	mo.observe(document.body, { childList: true });
	if (!mine.size) { mo.disconnect(); settle(); } // the bundle chose not to show anything
}

// Pure decision (unit-tested). rec = the stored record or null, legacy = the v1 tour key is
// set, env = { hasData, inOS, play, start }. A record from before RESHOW_BELOW counts as a
// fresh start (its offers reset); anything else must still be waiting for an answer.
export function shouldOffer(rec, legacy, env) {
	if (legacy || !env || env.inOS || env.play || env.start === false) return false;
	if (!rec || typeof rec !== "object") return !env.hasData;
	if (!(rec.v >= ONBOARDING_RESHOW_BELOW)) return true;
	return live(rec) && (rec.offers | 0) < ONBOARDING_MAX_OFFERS;
}

// A first visit that cannot be offered right now (a /?play= link, inside the desktop) is still
// a first visit: remember it as pending, so the book it opens (history, session, recents) does
// not make the next plain visit look like a returning one. Pure, unit-tested.
export function shouldRemember(rec, legacy, env) {
	if (legacy || !env || env.hasData || !(env.play || env.inOS)) return false;
	return !rec || typeof rec !== "object" || !(rec.v >= ONBOARDING_RESHOW_BELOW);
}

// Is the first-run welcome behind this visitor? Finished the v1 tour, answered the welcome (done
// or dismissed), or a returning visitor from before it existed. The start page bumper waits for
// this (bumper-gate.js), and the pre-paint in build.mjs (BUMPER_PREPAINT) has the same rule so
// the bumper's room is held exactly when it will fill. Read at boot, before anything writes.
export function welcomeBehind(ls = localStorage) {
	try {
		const rec = JSON.parse(ls.getItem(ONBOARDING_KEY)); // a broken record: false, like the pre-paint
		if (ls.getItem(ONBOARDING_LEGACY_KEY) != null) return true;
		if (rec) return rec.v >= ONBOARDING_RESHOW_BELOW && /^d/.test(rec.status);
		return [SETTINGS_KEY, FAVORITES_KEY, HISTORY_KEY, SESSION_KEY].some((k) => ls.getItem(k) != null);
	} catch { return false; }
}

// Boot-time snapshot, taken before anything in this visit writes to storage.
export function checkOnboarding() {
	try {
		const ls = localStorage;
		ls.setItem(ONBOARDING_KEY + ":t", "1"); ls.removeItem(ONBOARDING_KEY + ":t"); // storage must work, or we could not remember the answer
		if (ls.getItem(ONBOARDING_LEGACY_KEY) != null) return null; // finished the v1 tour
		try { if (sessionStorage.getItem(ONBOARDING_KEY + ":seen") === "snoozed") return null; } catch { /* ignore */ } // "Not now" holds for this sitting
		let rec = readJSON(ONBOARDING_KEY, null);
		if (!rec || typeof rec !== "object") rec = null;
		if (rec && rec.v >= ONBOARDING_RESHOW_BELOW) {
			if (!live(rec)) return null; // done or dismissed
			if ((rec.offers | 0) >= ONBOARDING_MAX_OFFERS) { // offered enough times with no answer
				writeJSON(ONBOARDING_KEY, Object.assign({}, rec, { status: "dismissed", at: Date.now() }));
				return null;
			}
		}
		return { rec, hasData: [SETTINGS_KEY, FAVORITES_KEY, HISTORY_KEY, SESSION_KEY].some((k) => ls.getItem(k) != null) };
	} catch { return null; }
}

// ctx: { inOS, play, isStartVisible(), hasLiveTabs(), openDesktop(), openLooks(), searchInput,
// startEl, get, setMany }. Everything the lazy bundle needs is handed over here, so it shares
// this page's settings store instead of bundling a second copy. Returns true while onboarding
// is live on this visit (the home card then stays away, docs/HOME-2026-10.md F6).
export function initOnboarding(snap, ctx) {
	if (snap && shouldRemember(snap.rec, false, { hasData: snap.hasData, inOS: ctx.inOS, play: ctx.play })) {
		writeJSON(ONBOARDING_KEY, { v: ONBOARDING_VERSION, status: "pending", offers: 0, at: Date.now() });
		return true;
	}
	if (!snap || !shouldOffer(snap.rec, false, { hasData: snap.hasData, inOS: ctx.inOS, play: ctx.play, start: ctx.isStartVisible() })) return false;
	let rec = snap.rec;
	welcome = "waiting";
	// An eligible first visit is remembered at once, so opening a book before the chip appears
	// (which saves the session) does not turn the next visit into a "returning" one.
	if (!rec || !(rec.v >= ONBOARDING_RESHOW_BELOW)) {
		rec = { v: ONBOARDING_VERSION, status: "pending", offers: 0, at: Date.now() };
		writeJSON(ONBOARDING_KEY, rec);
	}
	let idle = false, touched = false, quiet = 0;
	const evs = ["pointerdown", "keydown", "focusin"];
	const touch = (e) => { if (e.type !== "focusin" || /^(INPUT|TEXTAREA)$/.test(e.target.tagName)) touched = true; };
	const stop = () => { clearInterval(iv); evs.forEach((t) => removeEventListener(t, touch, true)); };
	const leave = () => { stop(); settle(); }; // left the start screen: no welcome on this visit
	evs.forEach((t) => addEventListener(t, touch, { capture: true, passive: true }));
	const onLoad = () => (window.requestIdleCallback ? requestIdleCallback(() => { idle = true; }, { timeout: 4000 }) : setTimeout(() => { idle = true; }, 200));
	if (document.readyState === "complete") onLoad(); else addEventListener("load", onLoad, { once: true });
	const iv = setInterval(() => {
		if (!ctx.isStartVisible()) return leave(); // opened a tab, book, tool or Settings: not this visit
		const now = performance.now();
		if (!idle || now < 2500 || document.visibilityState !== "visible") return;
		if (touched) {
			// Already busy: wait until the search box has been empty and unfocused for 4 s.
			const a = document.activeElement;
			if (ctx.searchInput.value || (a && (/^(INPUT|TEXTAREA)$/.test(a.tagName) || a.isContentEditable))) { quiet = 0; return; }
			if (!quiet) quiet = now;
			if (now - quiet < 4000) return;
		}
		stop();
		welcome = "shown";
		const css = document.createElement("link");
		css.rel = "stylesheet"; css.href = __RVM.onbCss;
		document.head.appendChild(css);
		const url = __RVM.onbJs;
		import(url).then((m) => {
			let before = new Set();
			try { before = new Set(document.body.children); } catch { /* no body to watch */ }
			m.start(Object.assign({ rec, read: () => readJSON(ONBOARDING_KEY, rec), write: (r) => writeJSON(ONBOARDING_KEY, r) }, ctx));
			try { watchWelcome(before); } catch { settle(); }
		}).catch(() => settle());
	}, 250);
	return true;
}
